import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { View } from "@common/types/views/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    AccrualMatchColumn,
    AccrualMatchSortValue,
    AccrualMatchTableCallbacks,
    AccrualMatchSection,
    MatchableItem,
    MatchItemType,
} from "./types";
import { createAccrualMatchViewConfig } from "./accrualMatchViewConfig";
import { useAccrualMatchView } from "./useAccrualMatchView";
import { useAccrualMatch } from "./useAccrualMatch";
import styles from "./AccrualMatchTable.module.scss";

interface AccrualMatchTableOptions {
    section: AccrualMatchSection;
    actionLabel?: string;
    /**
     * Whether period-lock gating applies to this table's row actions
     * (PRO-12200). Only the views that render the LOCK column (the primary
     * workspace-matched drawer) gate Match/Unmatch, so a locked row's disabled
     * action always has the accompanying lock icon to explain it. The legacy
     * reverse-match drawer view has no LOCK column and so no gating.
     */
    isLockGatingEnabled: boolean;
}

interface Props {
    data: MatchableItem[];
    section: AccrualMatchSection;
    onAction?: (item: MatchableItem) => void;
    actionLabel?: string;
    emptyView?: () => React.ReactNode;
    fixedTopOffsetPx?: number;
    matchItemType?: MatchItemType;
    sort?: AccrualMatchSortValue;
    onSortChange?: (sort: AccrualMatchSortValue) => void;
    footer?: React.ReactNode;
    hideHeader?: boolean;
    transparentRows?: boolean;
    className?: string;
}

export const AccrualMatchTable: React.FC<Props> = ({
    data,
    section,
    onAction,
    actionLabel,
    fixedTopOffsetPx,
    emptyView,
    matchItemType,
    sort,
    onSortChange,
    footer,
    hideHeader,
    transparentRows,
    className,
}) => {
    const { tableView, setTableView } = useAccrualMatchView();
    const { ledgerBasis, isGlFirst } = useAccrualMatch();

    const isLockGatingEnabled = useMemo(
        () =>
            tableView.columns.some(
                (column) => column.id === AccrualMatchColumn.LOCK,
            ),
        [tableView],
    );

    const tableMeta = useMemo<
        ViewTableMeta<AccrualMatchTableCallbacks, AccrualMatchTableOptions>
    >(
        () => ({
            callbacks: { onAction },
            options: {
                section,
                actionLabel,
                isLockGatingEnabled,
            },
        }),
        [onAction, section, actionLabel, isLockGatingEnabled],
    );

    const config = useMemo(
        () =>
            createAccrualMatchViewConfig(matchItemType ?? null, {
                ledgerBasis,
                isGlFirst,
            }),
        [matchItemType, ledgerBasis, isGlFirst],
    );

    // The search section's table sits flush with the drawer edges so its row
    // dividers extend full-width. We add invisible spacer columns at the
    // leading/trailing ends to push content inward to match the inset of the
    // matches-panel tables (see SpacerCell + LEFT_SPACER/RIGHT_SPACER in
    // accrualMatchViewConfig.ts).
    const viewWithSpacers = useMemo<View>(() => {
        if (section !== "search") {
            return tableView;
        }
        return {
            ...tableView,
            columns: [
                {
                    id: AccrualMatchColumn.LEFT_SPACER,
                    options: { pin: "left" },
                },
                ...tableView.columns,
                {
                    id: AccrualMatchColumn.RIGHT_SPACER,
                    options: { pin: "right" },
                },
            ],
        };
    }, [tableView, section]);

    // Strip the search-only spacer columns before persisting view changes so
    // they don't leak into the cached view shared with the matches-panel
    // tables. Resizing/reordering still works for real columns.
    const handleViewChange = useCallback(
        (newView: View) => {
            setTableView({
                ...newView,
                columns: newView.columns.filter(
                    (col) =>
                        col.id !== AccrualMatchColumn.LEFT_SPACER &&
                        col.id !== AccrualMatchColumn.RIGHT_SPACER,
                ),
            });
        },
        [setTableView],
    );

    return (
        <ViewTable
            className={classNames(
                styles.rightAlignedAmounts,
                hideHeader && styles.hideHeader,
                transparentRows && styles.transparentRows,
                // Matches-panel tables (source/selected) have no spacer
                // columns, so reset the wider :first-child / :last-child cell
                // paddings to the default cell padding to keep their data
                // column grid aligned with the search table's data columns.
                section !== "search" && styles.flushCellEdges,
                className,
            )}
            data={data}
            view={viewWithSpacers}
            onViewChange={handleViewChange}
            viewChangeDebounceMs={0}
            config={config}
            tableMeta={tableMeta}
            emptyView={emptyView}
            rowBorders
            fixedTopOffsetPx={fixedTopOffsetPx}
            sort={sort}
            onSortChange={onSortChange}
            footer={footer}
        />
    );
};
